import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api";
import { isRecord } from "../payloadGuards";

// Types
export interface Campaign {
  id?: string;
  name: string;
  practice_id: string;
  status: 'active' | 'paused' | 'pending';
  daily_budget: number;
  start_date: string;
  end_date?: string;
  target_audience?: {
    locations: string[];
    demographics: {
      age_ranges: string[];
      genders: string[];
    };
    interests: string[];
  };
  created_at?: string;
  updated_at?: string;
}

export interface AssetGroup {
  id?: string;
  campaign_id: string;
  name: string;
  headlines: string[];
  descriptions: string[];
  images: string[];
  final_urls: string[];
  status: 'active' | 'paused' | 'pending';
  created_at?: string;
  updated_at?: string;
}

export interface CreateCampaignPayload {
  name: string;
  practice_id: string;
  daily_budget: number;
  start_date: string;
  end_date?: string;
  target_audience?: {
    locations: string[];
    demographics: {
      age_ranges: string[];
      genders: string[];
    };
    interests: string[];
  };
}

export interface UpdateCampaignPayload {
  id: string;
  name?: string;
  status?: 'active' | 'paused';
  daily_budget?: number;
  end_date?: string;
  target_audience?: {
    locations: string[];
    demographics: {
      age_ranges: string[];
      genders: string[];
    };
    interests: string[];
  };
}

export interface CreateAssetGroupPayload {
  campaign_id: string;
  name: string;
  headlines: string[];
  descriptions: string[];
  images: string[];
  final_urls: string[];
}

export interface UpdateAssetGroupPayload {
  campaign_id: string;
  asset_group_id: string;
  name?: string;
  headlines?: string[];
  descriptions?: string[];
  images?: string[];
  final_urls?: string[];
  status?: 'active' | 'paused';
}

interface CampaignState {
  campaigns: Campaign[];
  assetGroups: AssetGroup[];
  selectedCampaign: Campaign | null;
  selectedAssetGroup: AssetGroup | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  isLoading: boolean;
}

const initialState: CampaignState = {
  campaigns: [],
  assetGroups: [],
  selectedCampaign: null,
  selectedAssetGroup: null,
  status: 'idle',
  error: null,
  isLoading: false,
};

// Async thunks
export const createCampaign = createAsyncThunk(
  'campaigns/createCampaign',
  async (campaignData: CreateCampaignPayload) => {

    try {
      const response = await api.post('/marketing/google/campaign', campaignData);
      return response.data.body;
    } catch (error) {
      let errorMessage = "Failed to create campaign";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async (campaignData: UpdateCampaignPayload) => {
    try {
      const response = await api.patch('/marketing/google/campaign', campaignData);
      return response.data.body;
    } catch (error) {
      let errorMessage = "Failed to update campaign";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }
);

export const createAssetGroup = createAsyncThunk(
  'campaigns/createAssetGroup',
  async (assetGroupData: CreateAssetGroupPayload) => {
    try {
      const response = await api.post(`/marketing/google/campaign/${assetGroupData.campaign_id}/assetgroup`, assetGroupData);
      return response.data.body;
    } catch (error) {
      let errorMessage = "Failed to create asset group";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }
);

export const updateAssetGroup = createAsyncThunk(
  'campaigns/updateAssetGroup',
  async (assetGroupData: UpdateAssetGroupPayload) => {
    try {
      const response = await api.patch(`/marketing/google/campaign/${assetGroupData.campaign_id}/assetgroup`, assetGroupData);
      return response.data.body;
    } catch (error) {
      let errorMessage = "Failed to update asset group";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }
);

const campaignSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCampaign: (state, action) => {
      state.selectedCampaign = action.payload;
    },
    setSelectedAssetGroup: (state, action) => {
      state.selectedAssetGroup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Campaign
      .addCase(createCampaign.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoading = false;
        if (isRecord(action.payload)) state.campaigns.push(action.payload);
        state.selectedCampaign = action.payload;
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create campaign';
      })
      
      // Update Campaign
      .addCase(updateCampaign.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoading = false;
        const updatedCampaign = action.payload;
        state.campaigns = state.campaigns.map(campaign => 
          campaign.id === updatedCampaign.id ? updatedCampaign : campaign
        );
        if (state.selectedCampaign?.id === updatedCampaign.id) {
          state.selectedCampaign = updatedCampaign;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update campaign';
      })
      
      // Create Asset Group
      .addCase(createAssetGroup.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAssetGroup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoading = false;
        if (isRecord(action.payload)) state.assetGroups.push(action.payload);
        state.selectedAssetGroup = action.payload;
      })
      .addCase(createAssetGroup.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create asset group';
      })
      
      // Update Asset Group
      .addCase(updateAssetGroup.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssetGroup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isLoading = false;
        const updatedAssetGroup = action.payload;
        state.assetGroups = state.assetGroups.map(assetGroup => 
          assetGroup.id === updatedAssetGroup.id ? updatedAssetGroup : assetGroup
        );
        if (state.selectedAssetGroup?.id === updatedAssetGroup.id) {
          state.selectedAssetGroup = updatedAssetGroup;
        }
      })
      .addCase(updateAssetGroup.rejected, (state, action) => {
        state.status = 'failed';
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update asset group';
      });
  },
});

export const { clearError, setSelectedCampaign, setSelectedAssetGroup } = campaignSlice.actions;
export default campaignSlice.reducer;
